/**
 * Utility functions for UploadLoaderModal
 * 
 * Pure functions and helpers extracted for reusability and testing
 */

import { startCase } from "lodash";
import { ProcessingStep } from './types';

/**
 * Convert any string to proper sentence case
 */
export const toSentenceCase = (str: string): string => {
  if (!str) return '';
  // Convert to lowercase first, then apply startCase to handle all-caps inputs
  return startCase(str.toLowerCase());
};

/**
 * Generate a unique step ID
 */
export const generateStepId = (prefix: string = 'step'): string => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Check if user is at bottom of scrollable container
 */
export const isAtBottom = (element: HTMLDivElement, threshold: number = 50): boolean => {
  const { scrollTop, scrollHeight, clientHeight } = element;
  return scrollTop + clientHeight >= scrollHeight - threshold;
};

/**
 * Scroll element to bottom
 */
export const scrollToBottom = (element: HTMLDivElement): void => {
  element.scrollTop = element.scrollHeight;
};

/**
 * Create an initial upload step
 */
export const createInitialUploadStep = (): ProcessingStep => ({
  id: 'upload-files',
  label: 'Uploading files...',
  status: 'processing',
  timestamp: undefined
});

/**
 * Mark all steps as completed
 */
export const markAllStepsCompleted = (steps: ProcessingStep[]): ProcessingStep[] => {
  return steps.map(step => ({
    ...step,
    status: 'completed' as const,
    timestamp: step.timestamp || new Date().toLocaleTimeString()
  }));
};

/**
 * Convert backend step status to frontend status
 * Ensures only the last step can be "processing"
 */
export const convertBackendStepsToFrontend = (
  backendSteps: any[],
  preserveProcessingStatus: boolean = false
): ProcessingStep[] => {
  return backendSteps.map((step, index, array) => {
    const isLastStep = index === array.length - 1;
    const shouldBeProcessing = preserveProcessingStatus && isLastStep && step.status === 'processing';
    const finalStatus = shouldBeProcessing ? 'processing' : 'completed';
    
    // Map timestamp from backend fields
    let timestamp: string | undefined;
    if (finalStatus === 'completed' && step.endTime) {
      // Use endTime for completed steps
      timestamp = new Date(step.endTime).toLocaleTimeString();
    } else if (finalStatus === 'completed' && step.startTime) {
      // Fallback to startTime if no endTime for completed steps
      timestamp = new Date(step.startTime).toLocaleTimeString();
    }
    // No timestamp for processing steps (they're still in progress)

    return {
      id: step.stepId,
      label: step.label,
      status: finalStatus,
      timestamp
    };
  });
};

/**
 * Find step by label (case-insensitive)
 */
export const findStepByLabel = (steps: ProcessingStep[], label: string): ProcessingStep | undefined => {
  return steps.find(step => 
    step.label.toLowerCase() === label.toLowerCase()
  );
};

/**
 * Find step by ID
 */
export const findStepById = (steps: ProcessingStep[], id: string): ProcessingStep | undefined => {
  return steps.find(step => step.id === id);
};

/**
 * Update or add a step to the steps array
 */
export const updateOrAddStep = (
  currentSteps: ProcessingStep[],
  stepUpdate: { label: string; status: 'pending' | 'processing' | 'completed'; id?: string }
): ProcessingStep[] => {
  const existingIndex = currentSteps.findIndex(step => 
    step.label.toLowerCase() === stepUpdate.label.toLowerCase() ||
    (stepUpdate.id && step.id === stepUpdate.id)
  );
  
  if (existingIndex !== -1) {
    // Update existing step
    return currentSteps.map((step, index) => {
      if (index === existingIndex) {
        return {
          ...step,
          status: stepUpdate.status,
          timestamp: stepUpdate.status === 'completed' ? new Date().toLocaleTimeString() : step.timestamp
        };
      }
      // Mark previous steps as completed
      if (index < existingIndex && step.status !== 'completed') {
        return {
          ...step,
          status: 'completed' as const,
          timestamp: step.timestamp || new Date().toLocaleTimeString()
        };
      }
      return step;
    });
  } else {
    // Add new step
    const newStep: ProcessingStep = {
      id: stepUpdate.id || generateStepId(),
      label: stepUpdate.label,
      status: stepUpdate.status,
      timestamp: stepUpdate.status === 'completed' ? new Date().toLocaleTimeString() : undefined
    };
    
    // Mark all previous steps as completed and add new step
    const completedSteps = currentSteps.map(step => ({
      ...step,
      status: 'completed' as const,
      timestamp: step.timestamp || new Date().toLocaleTimeString()
    }));
    
    return [...completedSteps, newStep];
  }
};

/**
 * Validate job ID format (UUID v4)
 */
export const isValidJobId = (jobId: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(jobId);
};

/**
 * Check if a URL is a full URL (starts with http/https)
 */
export const isFullUrl = (url: string): boolean => {
  return url.startsWith('http://') || url.startsWith('https://');
};

/**
 * Create a download link and trigger download
 */
export const triggerDownload = (url: string, filename: string = 'document'): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Calculate progress percentage
 */
export const calculateProgress = (current: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((current / total) * 100);
};

/**
 * Debounce function for performance optimization
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  };
}; 